import { useState, useEffect, useMemo } from 'react';
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    doc,
    updateDoc,
    writeBatch,
    arrayUnion
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { getMessaging, getToken } from 'firebase/messaging';
import { useAuth } from '@/features/auth/AuthProvider';
import type { Notification } from '@/types';

export const useNotifications = (targetUserId?: string) => {
    const { currentUser } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(true);

    // Determine target user
    const userId = targetUserId || currentUser?.uid;

    // Clear state on logout
    useEffect(() => {
        if (!userId) {
            // eslint-disable-next-line
            setNotifications([]);
            setLoading(false);
        }
    }, [userId]);

    // Subscribe to notifications
    useEffect(() => {
        if (!userId) return;

        // eslint-disable-next-line
        setLoading(true);

        const q = query(
            collection(db, 'notifications'),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const newNotifications = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate() || new Date(),
            })) as Notification[];

            setNotifications(newNotifications);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching notifications:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userId]);

    const unreadCount = useMemo(() => {
        return notifications.filter(n => !n.isRead).length;
    }, [notifications]);

    const markAsRead = async (notificationId: string) => {
        if (!currentUser) return;
        try {
            const notifRef = doc(db, 'notifications', notificationId);
            await updateDoc(notifRef, { isRead: true });
        } catch (error) {
            console.error("Error marking notification as read:", error);
        }
    };

    const markAllAsRead = async () => {
        if (!currentUser || unreadCount === 0) return;

        try {
            const batch = writeBatch(db);
            const unreadNotifications = notifications.filter(n => !n.isRead);

            unreadNotifications.forEach(n => {
                const notifRef = doc(db, 'notifications', n.id);
                batch.update(notifRef, { isRead: true });
            });

            await batch.commit();
        } catch (error) {
            console.error("Error marking all as read:", error);
        }
    };

    const requestPermission = async () => {
        if (!currentUser) return false;

        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                const messaging = getMessaging();
                const token = await getToken(messaging); // Default config used

                if (token) {
                    await saveToken(token);
                    return true;
                }
            }
        } catch (error) {
            console.error("Error requesting notification permission:", error);
        }
        return false;
    };

    const saveToken = async (token: string) => {
        if (!currentUser) return;
        try {
            const userRef = doc(db, 'users', currentUser.uid);
            await updateDoc(userRef, {
                fcmTokens: arrayUnion(token)
            });
        } catch (error) {
            console.error("Error saving FCM token:", error);
        }
    };

    return {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        requestPermission
    };
};
